@if ((user$ | async)) {
<p-accordion
	[multiple]="false"
	[activeIndex]="activeTab$ | async"
	(activeIndexChange)="onTabChange($event)"
>
	<p-accordionTab header="Details">
		<div class="p-fluid p-formgrid p-grid">
			<div class="p-field p-col-12 p-md-6">
				<label
					for="firstname"
					class="p-mb-2 inline-block font-medium"
				>First Name</label>
				<input
					pInputText
					id="firstname"
					aria-describedby="firstname-help"
					[ngModel]="(user$ | async).givenName"
					(input)="updateUser({ givenName: $event.target.value })"
				/>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="lastname"
					class="p-mb-2 inline-block font-medium"
				>Last Name</label>
				<input
					pInputText
					id="lastname"
					aria-describedby="lastname-help"
					[ngModel]="(user$ | async).familyName"
					(input)="updateUser({ familyName: $event.target.value })"
				/>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="email"
					class="p-mb-2 inline-block font-medium"
				>Email</label>
				<input
					pInputText
					id="email"
					aria-describedby="email-help"
					[ngModel]="(user$ | async).email"
					(ngModelChange)="onEmailChange($event)"
					[ngClass]="{'p-invalid': isEmailInvalid}"
				/>
				@if(isEmailInvalid) {
				<small class="p-error block">Invalid email format</small>
				}
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="phone"
					class="p-mb-2 inline-block font-medium"
				>Phone Number</label>
				<p-inputMask
					#phoneMask
					type="tel"
					[ngModel]="(user$ | async).phone"
					(ngModelChange)="onPhoneChange($event)"
					[autoClear]="false"
					[unmask]="true"
					mask="(999) 999-9999"
					data-cy="phone-number-field"
					styleClass="full-width"
					[ngClass]="{'p-invalid': isPhoneInvalid}"
				/>
				<small
					*ngIf="isPhoneInvalid"
					class="p-error block"
				>Expected format: (xxx) xxx-xxxx</small>
			</div>
			<div class="p-field p-col-12">
				<label class="p-mb-2 inline-block font-medium">Capabilities</label>
				<p-multiSelect
					[options]="allCapabilities"
					[(ngModel)]="selectedCapabilities"
					optionLabel="label"
					placeholder="Select Capabilities"
					(onChange)="onCapabilitiesChange()"
					display="chip"
					maxSelectedLabels="4"
				/>
			</div>
			<!-- Performance Score (ops manager only) -->
		<ng-container *ngIf="canManagePerformance$ | async">
			<ng-container *ngIf="user$ | async as user">
				<div class="p-field p-col-12">
					<label class="p-mb-2 inline-block font-medium">
						Performance Score
						<i
							class="pi pi-info-circle p-ml-1"
							pTooltip="Crew quality score from 0.0 (any) to 1.0 (top). Used by the route optimizer to balance cost vs performance."
						></i>
					</label>
					<div class="p-d-flex p-ai-center" style="gap: 1rem;">
					<!-- D-8 (Maria): label the slider steps so the value is visible
					     without editing (autosave made that risky). -->
					<div class="perf-slider-with-steps" style="flex: 1;">
						<p-slider
							[ngModel]="user.performanceScore ?? 0.4"
							(onSlideEnd)="updateUser({ performanceScore: $event.value })"
							[min]="0"
							[max]="1"
							[step]="0.2"
							style="width: 100%;"
						></p-slider>
						<div class="perf-slider-steps" aria-hidden="true">
							<span *ngFor="let step of performanceSliderSteps">{{ step }}</span>
						</div>
					</div>
						<span class="font-medium" style="min-width: 110px; white-space: nowrap;">
							{{ getPerformanceScoreLabel(user.performanceScore) }}
						</span>
					</div>
				</div>
			</ng-container>
		</ng-container>

		<div class="p-field p-col-12 p-md-6">
				<label
					for="rateHourly"
					class="p-mb-2 inline-block font-medium"
				>Hourly Rate</label>
				<p-inputNumber
					[min]="0"
					[max]="10000"
					inputId="rateHourly"
					[ngModel]="(user$ | async).rateHourly / 100"
					[showButtons]="false"
					mode="currency"
					currency="USD"
					currencyDisplay="narrowSymbol"
					(onInput)="updateUser({ rateHourly: $event.value * 100 })"
				></p-inputNumber>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="overtimeRate"
					class="p-mb-2 inline-block font-medium"
				>Overtime Rate Multiplier</label>
				<p-inputNumber
					[min]="1"
					[max]="10"
					[step]="0.1"
					[minFractionDigits]="1"
					[maxFractionDigits]="2"
					inputId="overtimeRate"
					[ngModel]="(user$ | async).overtimeRate"
					[showButtons]="false"
					(onInput)="updateUser({ overtimeRate: $event.value })"
				></p-inputNumber>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="maxHoursPerWeek"
					class="p-mb-2 inline-block font-medium"
				>Max hours per week</label>
				<p-inputNumber
					[min]="0"
					[max]="168"
					[step]="0.5"
					[minFractionDigits]="1"
					[maxFractionDigits]="2"
					inputId="maxHoursPerWeek"
					[ngModel]="(user$ | async).maxHoursPerWeek"
					[showButtons]="false"
					(onInput)="updateUser({ maxHoursPerWeek: $event.value })"
				></p-inputNumber>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="maxHoursPerDay"
					class="p-mb-2 inline-block font-medium"
				>Max hours per day</label>
				<p-inputNumber
					[min]="0"
					[max]="24"
					[step]="0.5"
					[minFractionDigits]="1"
					[maxFractionDigits]="2"
					inputId="maxHoursPerDay"
					[ngModel]="(user$ | async).maxHoursPerDay"
					[showButtons]="false"
					(onInput)="updateUser({ maxHoursPerDay: $event.value })"
				></p-inputNumber>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label
					for="payPeriodStart"
					class="p-mb-2 inline-block font-medium"
				>Pay Period Start</label>
				<p-dropdown
					[options]="payPeriodOptions"
					[ngModel]="(user$ | async).payPeriodStart"
					optionLabel="label"
					optionValue="value"
					appendTo="body"
					(onChange)="updateUser({ payPeriodStart: $event.value })"
				></p-dropdown>
			</div>
			<div class="p-field p-col-12 p-md-6">
				<label class="p-mb-2 inline-block font-medium">Timezone</label>
				<p-dropdown
					[options]="AVAILABLE_TIMEZONES"
					[ngModel]="(user$ | async)?.timezone"
					optionLabel="label"
					optionValue="value"
					(onChange)="onTimezoneChanged($event.value)"
					appendTo="body"
				>
				</p-dropdown>
			</div>

		</div>
	</p-accordionTab>

	<p-accordionTab header="Availability">
		<div class="p-d-flex p-flex-column availability-section">
			<div class="p-d-flex p-ai-center p-flex-wrap p-mb-4">
				<p-checkbox
					[ngModel]="isAvailableChecked"
					[binary]="true"
					[inputId]="check-available-for-assignment"
					(onChange)="onAvailableForAssignmentChange($event)"
					class="p-mr-3"
				></p-checkbox>
				<label
					[for]="check-available-for-assignment"
					class="p-mr-3"
					[style]="{'font-weight': '500'}"
				>
					Available for Assignment
				</label>
			</div>

			<!-- Animation Container -->
			<div
				class="availability-rows-container"
				[class.visible]="isAvailableChecked"
			>
				@for (row of availabilityRows; track row.day) {
				<div class="p-d-flex p-ai-center p-flex-wrap p-mb-4">
					<p-checkbox
						[(ngModel)]="row.isEnabled"
						[binary]="true"
						[inputId]="'check-' + row.day"
						(onChange)="onAvailabilityChange()"
						class="p-mr-3"
						[disabled]="!isAvailableChecked"
					></p-checkbox>
					<label
						[for]="'check-' + row.day"
						class="p-mr-3"
						[style]="{'width': '6rem', 'font-weight': '500'}"
						[ngClass]="{'p-text-secondary': !row.isEnabled || !isAvailableChecked}"
					>
						{{ row.day }}
					</label>
					<p-calendar
						[(ngModel)]="row.start"
						[timeOnly]="true"
						hourFormat="12"
						[stepMinute]="15"
						[disabled]="!row.isEnabled || !(user$ | async).available"
						(onSelect)="onAvailabilityChange()"
						[style]="{'width': '100px'}"
						[inputStyle]="{'width': '100px'}"
						dataType="string"
						class="p-mr-3"
					></p-calendar>
					<span
						class="p-text-sm p-mr-3"
						[ngClass]="{'p-text-secondary': !row.isEnabled}"
					>
						to
					</span>
					<p-calendar
						[(ngModel)]="row.end"
						[timeOnly]="true"
						hourFormat="12"
						[stepMinute]="15"
						[disabled]="!row.isEnabled || !(user$ | async).available"
						(onSelect)="onAvailabilityChange()"
						[style]="{'width': '100px'}"
						[inputStyle]="{'width': '100px'}"
						dataType="string"
					></p-calendar>
				</div>
				}
			</div>
		</div>
	</p-accordionTab>

	<p-accordionTab header="Time Off">
		<!-- TIME OFF SECTION: Wrapped in a Card -->
		<div class="time-off-form-card">
			<div class="p-fluid p-formgrid p-grid">

				<!-- Date Field: 50% usually, 25% if custom -->
				<div
					class="p-field p-col-12"
					[ngClass]="{'p-md-6': !bookOffInput.selectedTimeBlock?.custom, 'p-md-3': bookOffInput.selectedTimeBlock?.custom}"
				>
					<label class="p-mb-2 inline-block font-medium">Date</label>
					<p-calendar
						#dateSelect
						[(ngModel)]="bookOffInput.selectedDate"
						dateFormat="DD, MM, d"
						appendTo="body"
						selectionMode="single"
						(ngModelChange)="onDateChanged($event)"
					></p-calendar>
				</div>

				<!-- Time Selection Field: 50% usually, 25% if custom -->
				<div
					class="p-field p-col-12"
					[ngClass]="{'p-md-6': !bookOffInput.selectedTimeBlock?.custom, 'p-md-3': bookOffInput.selectedTimeBlock?.custom}"
				>
					<label class="p-mb-2 inline-block font-medium">Time</label>
					<p-dropdown
						[options]="bookOffOptions"
						[(ngModel)]="bookOffInput.selectedTimeBlock"
						optionLabel="label"
						(onChange)="onTimeBlockChanged($event.value)"
						appendTo="body"
					>
						<ng-template
							let-item
							pTemplate="item"
						>
							<div>
								<span>{{ item.label }}</span>
								<br />
								<small *ngIf="!item.custom">{{ item.start | secondsToTime }} -
									{{ item.end | secondsToTime }}</small>
							</div>
						</ng-template>
					</p-dropdown>
				</div>

				<!-- Custom Start/End Times (Conditional, 25% each) -->
				@if (bookOffInput.selectedTimeBlock?.custom) {
				<div class="p-field p-col-12 p-md-3">
					<label class="p-mb-2 inline-block font-medium">Start</label>
					<p-calendar
						[(ngModel)]="bookOffInput.customStartTime"
						(ngModelChange)="onCustomTimeChanged()"
						[timeOnly]="true"
						hourFormat="12"
						appendTo="body"
					></p-calendar>
				</div>
				<div class="p-field p-col-12 p-md-3">
					<label class="p-mb-2 inline-block font-medium">End</label>
					<p-calendar
						[(ngModel)]="bookOffInput.customEndTime"
						(ngModelChange)="onCustomTimeChanged()"
						[timeOnly]="true"
						hourFormat="12"
						appendTo="body"
					></p-calendar>
				</div>

				@if (customTimeInvalid) {
				<div class="p-col-12">
					<small class="p-error">End time must be after start time.</small>
				</div>
				}
				}

				<!-- Description Field (100%) -->
				<div class="p-field p-col-12">
					<label class="p-mb-2 inline-block font-medium">Notes (optional)</label>
					<textarea
						[(ngModel)]="bookOffInput.description"
						(ngModelChange)="onDescriptionChanged($event)"
						pInputTextarea
						rows="3"
						placeholder="Optional: Description for why this crew member is booked off"
					></textarea>
				</div>

			</div>

			<div class="footer p-mt-1">
				<button
					pButton
					icon="pi pi-check"
					label="Add Time Off"
					class="p-button-success"
					type="button"
					[disabled]="!canSubmitBookOff()"
					(click)="bookOff()"
				></button>
			</div>
		</div>

		<!-- List of existing Time Off events -->
		@for (bookOffEvent of userBookOffs$ | async; track trackBookOffEvent) {
		@if (isBookOffEventCurrentOrFuture(bookOffEvent)) {
		<p-card class="timeoff-event-card p-p-0 p-mb-3">
			<div class="timeoff-event-content p-inline p-p-0">

				<div class="timeoff-event-text">
					<div class="timeoff-event-title">{{ formatBookOffEventDate(bookOffEvent) }} ({{
						formatBookOffEventTime(bookOffEvent) }})</div>
					<div class="timeoff-event-description">
						{{ bookOffEvent.description || '' }}
					</div>
				</div>

				<p-button
					icon="pi pi-trash"
					aria-label="Delete book-off"
					[plain]="true"
					[text]="true"
					(click)="deleteBookOffEvent(bookOffEvent.id)"
				></p-button>

			</div>
		</p-card>
		}
		}
		@if (hasPastBookOffsIn(userBookOffs$ | async) && !showPastBookOffs) {
		<div class="p-mt-3 p-text-sm p-text-secondary">
			<p-button
				label="Show past time off"
				class="p-button-link"
				(type)="button"
				(click)="toggleShowPastBookOffs()"
				link
			></p-button>
		</div>
		}
	</p-accordionTab>
</p-accordion>
}